/**
 * Footer de assinatura da contratada (Karla + KM Gestão) usado no fim
 * da proposta pública e — desde 2026-04-25 — também na visão pós-aceite
 * do contrato (`/proposta/:token` quando `proposal.status='accepted'`
 * E contrato existe).
 *
 * **Por que extraído**: antes este footer ficava inline dentro do
 * `ProposalPublicView`. Quando o lead aceitava a proposta e voltava
 * pra página, o `ProposalPublicView` renderizava A PROPOSTA INTEIRA
 * de novo (hero + planos + valores + considerações), o que confundia
 * o lead — a proposta já tinha sido aceita, agora o foco era assinar
 * o contrato. Extrair o footer permite renderizar SÓ ele + a seção
 * de contrato, sem duplicar a proposta. Reportado pelo Alexandre
 * 2026-04-25.
 *
 * Visual: replica `<Band tone="footer">` do ProposalPublicView (gradient
 * `from-muted/40 to-muted/20` + linha accent dourada de separação no
 * topo + conteúdo centrado max-w-4xl). Não importa Band do
 * ProposalPublicView porque queremos manter este componente
 * standalone (evitar import cycle se algum dia alguém importar este
 * componente lá).
 */

interface Contratada {
  razao_social: string;
  cnpj: string;
  cidade_uf: string;
  email_contato: string;
  responsavel_nome: string;
  responsavel_cargo: string;
}

export function ContratadaSignatureFooter({ contratada }: { contratada: Contratada }) {
  // Replica `<Band tone="footer">` — gradient + linha accent no topo.
  // Centralização horizontal e padding combinam com o resto da página.
  const bandClasses =
    'relative w-full overflow-hidden bg-gradient-to-b from-muted/40 to-muted/20 ' +
    'before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-px ' +
    'before:bg-gradient-to-r before:from-transparent before:via-accent/40 before:to-transparent ' +
    'before:content-[""]';

  return (
    <section className={bandClasses}>
      <div className="relative z-10 mx-auto w-full max-w-4xl px-4 py-12 sm:px-8 sm:py-16">
        <footer className="space-y-2 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-muted-foreground">
            Atenciosamente
          </p>
          <p className="text-xl font-bold text-foreground">{contratada.responsavel_nome}</p>
          <p className="text-sm text-foreground">{contratada.responsavel_cargo}</p>
          <div className="mt-6 inline-flex flex-col gap-0.5 text-xs text-muted-foreground sm:flex-row sm:items-center sm:gap-3">
            <span className="font-medium text-foreground">{contratada.razao_social}</span>
            <span aria-hidden="true" className="hidden sm:inline">
              ·
            </span>
            <span>CNPJ {contratada.cnpj}</span>
            <span aria-hidden="true" className="hidden sm:inline">
              ·
            </span>
            <span>{contratada.cidade_uf}</span>
            <span aria-hidden="true" className="hidden sm:inline">
              ·
            </span>
            <a href={`mailto:${contratada.email_contato}`} className="text-accent hover:underline">
              {contratada.email_contato}
            </a>
          </div>
        </footer>
      </div>
    </section>
  );
}
